import { EditFilled } from '@ant-design/icons';
import { Alert, Col, Divider, Pagination, Row, Skeleton, Spin } from 'antd';
import { isEmpty } from 'lodash';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { CountTag } from '../../../components/common/CountTag';
import { panelHeaderTitle } from '../../../components/common/utils';
import {
  Button,
  Collapse,
  Descriptions,
  DockingLogStatus,
  FinancialOfficersTableDescription,
  FormatItem,
  Title,
} from '../../../components/index';
import { Docking as DockingType, DockingTypeDescription } from '../../../types';
import {
  getDockingEventEnd,
  getDockingEventStart,
  getDockingPlaceLabel,
  isAnchoringDocking,
  isDockingInProgress,
} from './dockingEventStart';
import { formatToDescription } from './formatDockingToDescription';
import { ProductDescription } from './productDescription';
import {
  DockingEventBlockCollapse,
  DockingItemsCollapse,
  DockingItemsLoading,
  TitleEdit,
} from './styles';

const { Item } = Descriptions;
const { Panel } = Collapse;

const LOADING_ROW_WIDTHS = ['70%', '55%', '62%'];

/** O que define um bloco da listagem, e o que o diferencia do outro. */
export type DockingEventBlock = {
  key: string;
  title: string;
  /** "Agora e próximas" nasce aberto; "Histórico" nasce colapsado. */
  defaultOpen: boolean;
  /** O que o bloco diz de si quando não tem nenhum evento. */
  emptyMessage: string;
  showEventEnd?: boolean;
};

type DockingDescriptionProps = {
  /** A lista já filtrada por estado, na ordem em que o backend a devolveu. */
  selectedDockings?: DockingType[] | null;
  block: DockingEventBlock;
  /** O total do conjunto no backend, que não é o tamanho da página exibida. */
  total?: number;
  isFetching?: boolean;
  isError?: boolean;
  /** Presentes só no bloco paginado. */
  page?: number;
  pageSize?: number;
  onChangePage?: (page: number) => void;
  onClickEdit: (evt: any, indexToEdit: number) => void;
  screen: 'STOPOVER' | 'STAYMENT' | 'AGENT_AREA';
  isDockingDrawerVisible: boolean;
  setOpenCollapsePanels: Dispatch<SetStateAction<string | string[]>>;
};

export function DockingDescription(props: DockingDescriptionProps) {
  const {
    selectedDockings,
    block,
    total,
    isFetching,
    isError,
    page,
    pageSize,
    onChangePage,
    onClickEdit,
    screen,
    isDockingDrawerVisible,
    setOpenCollapsePanels,
  } = props;
  const [dockingsDescription, setDockingsDescription] = useState<
    DockingTypeDescription[]
  >([]);

  useEffect(() => {
    setDockingsDescription(formatToDescription(selectedDockings));
  }, [selectedDockings, isDockingDrawerVisible]);

  function dockingCollapseHeader(index: number) {
    if (
      selectedDockings === null ||
      selectedDockings === undefined ||
      index >= selectedDockings.length
    ) {
      return;
    }
    const docking = selectedDockings[index];
    const headerTitle = isAnchoringDocking(docking) ? 'Fundeio' : 'Atracação';
    const eventStart = getDockingEventStart(docking);
    const eventEnd = block.showEventEnd ? getDockingEventEnd(docking) : null;
    const dockingPlace = docking.docking_place?.name;
    return (
      <div className="docking-header-title">
        <span className="docking-title">
          {`${headerTitle} `}
          <small>{(docking.index || 0) + 1}</small>
        </span>
        <span className="docking-code">
          {docking.code} - {docking.docking_goal?.description}
        </span>
        <span className={`docking-berth${dockingPlace ? '' : ' absent'}`}>
          {getDockingPlaceLabel(docking)}
        </span>
        {eventStart ? (
          <span
            className={`docking-event-date${
              eventStart.isReal ? '' : ' expected'
            }`}
          >
            {eventEnd && eventStart.isReal
              ? `${eventStart.date} até ${eventEnd.date}`
              : `${eventStart.label} ${eventStart.date}`}
          </span>
        ) : (
          <span className="docking-event-date absent">Sem data</span>
        )}
        {eventEnd && !eventStart?.isReal && (
          <span className="docking-event-date">
            {`${eventEnd.label} ${eventEnd.date}`}
          </span>
        )}
        {isDockingInProgress(docking) && (
          <span className="docking-in-progress">Acontecendo agora</span>
        )}
        <DockingLogStatus
          dockingLogStatus={docking.event_status || docking.status}
        />
      </div>
    );
  }

  function blockHeader() {
    return (
      <span className="docking-block-title">
        {block.title}
        {total === undefined ? (
          <Spin size="small" style={{ marginInlineStart: 8 }} />
        ) : (
          <CountTag>{total}</CountTag>
        )}
      </span>
    );
  }

  function blockPagination() {
    if (!onChangePage || (total || 0) <= (pageSize || 0)) {
      return null;
    }
    return (
      <Row style={{ marginBlockStart: 16, justifyContent: 'center' }}>
        <Col>
          <Pagination
            current={page}
            total={total}
            pageSize={pageSize}
            showSizeChanger={false}
            onChange={onChangePage}
          />
        </Col>
      </Row>
    );
  }

  function blockContent() {
    // O ErrorMiddleware já dispara o toast de toda action rejeitada, então aqui
    // o erro só precisa dizer, no bloco, que ele não carregou.
    if (isError) {
      return (
        <Alert
          type="error"
          showIcon
          message="Não foi possível carregar estes eventos. Tente novamente."
        />
      );
    }
    // isFetching, e não isLoading: ao trocar de página ou de escala o RTK Query
    // mantém o resultado anterior, que não é o desta consulta.
    if (isFetching) {
      return (
        <>
          <DockingItemsLoading>
            {LOADING_ROW_WIDTHS.map((width, row) => (
              <div className="docking-item-loading" key={`loading${row}`}>
                <Skeleton active title={false} paragraph={{ rows: 1, width }} />
              </div>
            ))}
          </DockingItemsLoading>
          {blockPagination()}
        </>
      );
    }
    if (isEmpty(dockingsDescription)) {
      return <Alert type="info" showIcon message={block.emptyMessage} />;
    }
    return (
      <>
        <DockingItemsCollapse expandIconPosition="end">
          {dockingsDescription?.map((docking, index) => {
            return (
              <Panel
                forceRender
                header={dockingCollapseHeader(index)}
                extra={
                  selectedDockings &&
                  index < selectedDockings.length &&
                  selectedDockings[index].status !== 'CANCELED' &&
                  panelHeaderTitle(docking.updated_at, onClickEdit, index)
                }
                key={`docking${index}`}
              >
                <div style={{ padding: '16px' }}>
                  {(docking.purpose_id === 'ANCHORING_SERVICES' ||
                    docking.purpose_id === 'ANCHORING_WAIT') && (
                    <Descriptions column={3}>
                      <Item label="Fundeadouro">
                        <FormatItem>{docking.docking_place}</FormatItem>
                      </Item>
                      <Item label="Finalidade" span={2}>
                        <FormatItem>{docking.purpose}</FormatItem>
                      </Item>
                      {screen === 'STAYMENT' && (
                        <Item label="Período em fundeio" span={3}>
                          <div>
                            <FormatItem>{docking.start_anchoring}</FormatItem>
                            <span> até </span>
                            <FormatItem>{docking.end_anchoring}</FormatItem>
                          </div>
                        </Item>
                      )}
                      {screen === 'STOPOVER' && (
                        <Item label="Previsão de estadia no fundeio" span={3}>
                          <div>
                            <FormatItem>
                              {docking.expected_anchoring}
                            </FormatItem>
                            <span> até </span>
                            <FormatItem>
                              {docking.expected_unanchoring}
                            </FormatItem>
                          </div>
                        </Item>
                      )}
                    </Descriptions>
                  )}
                  {docking.purpose_id === 'DOCK_IN_BERTH' && (
                    <>
                      {screen !== 'AGENT_AREA' && (
                        <>
                          <Descriptions
                            style={{ display: 'flex', marginBlockEnd: '-5px' }}
                          >
                            <Item label="Finalidade da atracação" span={2}>
                              <FormatItem>{docking.docking_goal}</FormatItem>
                            </Item>
                          </Descriptions>
                          <Divider />
                          <Title>OPERAÇÕES</Title>
                          <Descriptions column={3}>
                            <Item label="Natureza da carga" span={2}>
                              <FormatItem>
                                {docking.operation_summary?.nature_of_cargo}
                              </FormatItem>
                            </Item>
                            <Item label="Haverá inspeção?" span={1}>
                              <FormatItem>
                                {docking.operation_summary?.inspection}
                              </FormatItem>
                            </Item>
                            {screen === 'STAYMENT' && (
                              <>
                                <Item
                                  label="Previsão de todas operações"
                                  span={3}
                                >
                                  <div>
                                    <FormatItem>
                                      {
                                        docking.operation_summary
                                          ?.start_expected_operation
                                      }
                                    </FormatItem>
                                    <span> até </span>
                                    <FormatItem>
                                      {
                                        docking.operation_summary
                                          ?.end_expected_operation
                                      }
                                    </FormatItem>
                                  </div>
                                </Item>
                                <Item
                                  label="Período real de todas operações"
                                  span={3}
                                >
                                  <div>
                                    <FormatItem>
                                      {
                                        docking.operation_summary
                                          ?.start_real_time_of_operation
                                      }
                                    </FormatItem>
                                    <span> até </span>
                                    <FormatItem>
                                      {
                                        docking.operation_summary
                                          ?.end_real_time_of_operation
                                      }
                                    </FormatItem>
                                  </div>
                                </Item>
                              </>
                            )}
                          </Descriptions>
                        </>
                      )}
                      {screen !== 'AGENT_AREA' &&
                        docking.products &&
                        docking.products.length > 0 && (
                          <ProductDescription
                            products={docking.products}
                            screen={screen}
                          />
                        )}
                      {screen === 'AGENT_AREA' && (
                        <Alert
                          message={
                            <Descriptions column={2}>
                              <Item label="Berço" span={2}>
                                <FormatItem>{docking.docking_place}</FormatItem>
                              </Item>
                              <Item label="Previsão de atracação">
                                <FormatItem>
                                  {docking.expected_berthing}
                                </FormatItem>
                              </Item>
                              <Item label="Previsão de desatracação">
                                <FormatItem>
                                  {docking.expected_unberthing}
                                </FormatItem>
                              </Item>
                            </Descriptions>
                          }
                          type="info"
                        />
                      )}
                      <Divider />
                      <TitleEdit>
                        <Title>DADOS GERAIS</Title>
                        {screen === 'AGENT_AREA' && (
                          <Button
                            type="primary"
                            icon={<EditFilled />}
                            onClick={(e) => {
                              setOpenCollapsePanels('generalData');
                              onClickEdit(e, index);
                            }}
                          >
                            Editar
                          </Button>
                        )}
                      </TitleEdit>
                      {screen === 'AGENT_AREA' && (
                        <Descriptions column={2}>
                          <Item label="Finalidade da atracação" span={2}>
                            <FormatItem>{docking.docking_goal}</FormatItem>
                          </Item>
                          <Item label="Posição da atracação">
                            <FormatItem>{docking.position}</FormatItem>
                          </Item>
                          <Item label="Embarcação de apoio?">
                            <FormatItem>{docking.is_support_vessel}</FormatItem>
                          </Item>
                          <Item label="Atracação à contrabordo?">
                            <FormatItem>{docking.docking_alongside}</FormatItem>
                          </Item>
                          <Item label="Atracação em linha?">
                            <FormatItem>{docking.docking_in_line}</FormatItem>
                          </Item>
                        </Descriptions>
                      )}
                      <Descriptions column={3}>
                        {screen !== 'AGENT_AREA' && (
                          <>
                            <Item label="Berço" span={1}>
                              <FormatItem>{docking.docking_place}</FormatItem>
                            </Item>
                            <Item label="Atracação em linha?" span={2}>
                              <FormatItem>{docking.docking_in_line}</FormatItem>
                            </Item>
                            <Item label="Posição da atracação" span={1}>
                              <FormatItem>{docking.position}</FormatItem>
                            </Item>
                            <Item label="Atracação à contrabordo?" span={1}>
                              <FormatItem>
                                {docking.docking_alongside}
                              </FormatItem>
                            </Item>
                            <Item label="É embarcação de apoio?" span={1}>
                              <FormatItem>
                                {docking.is_support_vessel}
                              </FormatItem>
                            </Item>
                          </>
                        )}
                        {screen === 'AGENT_AREA' && (
                          <Item label="Duração da atracação" span={3}>
                            <div>
                              <FormatItem>{docking.start_tied}</FormatItem>
                              <span> até </span>
                              <FormatItem>{docking.end_untied}</FormatItem>
                            </div>
                          </Item>
                        )}
                        {['STOPOVER', 'AGENT_AREA'].includes(screen) && (
                          <>
                            <Item
                              label="Previsão de prático na atracação"
                              span={3}
                            >
                              <div>
                                <FormatItem>
                                  {docking.pilot_expected_on_board}
                                </FormatItem>
                                <span> até </span>
                                <FormatItem>
                                  {docking.pilot_expected_leaving_on_board}
                                </FormatItem>
                              </div>
                            </Item>
                            <Item
                              label="Previsão de prático na desatracação"
                              span={3}
                            >
                              <div>
                                <FormatItem>
                                  {docking.pilot_expected_leaving_on_board}
                                </FormatItem>
                                <span> até </span>
                                <FormatItem>
                                  {
                                    docking.pilot_expected_leaving_on_board_undocking
                                  }
                                </FormatItem>
                              </div>
                            </Item>
                            <Item label="Previsão de atracação" span={3}>
                              <div>
                                <FormatItem>
                                  {docking.expected_berthing}
                                </FormatItem>
                                <span> até </span>
                                <FormatItem>
                                  {docking.expected_unberthing}
                                </FormatItem>
                              </div>
                            </Item>
                            <Divider />
                          </>
                        )}
                      </Descriptions>

                      {screen === 'STAYMENT' && (
                        <>
                          <Title>MANOBRA DE ATRACAÇÃO</Title>
                          <Descriptions column={3}>
                            <Item label="Manobra confirmada?" span={3}>
                              {docking.docking_manoeuvre?.confirmed}
                            </Item>
                            <Item label="Calado de proa">
                              <FormatItem>
                                {docking.docking_manoeuvre?.bow_draught}
                              </FormatItem>
                            </Item>
                            <Item label="Calado de popa">
                              <FormatItem>
                                {docking.docking_manoeuvre?.stern_draught}
                              </FormatItem>
                            </Item>
                            <Item label="Calado na entrada">
                              <FormatItem>{docking.input_draught}</FormatItem>
                            </Item>
                          </Descriptions>
                          <Descriptions column={3}>
                            <Item label="DWT na entrada">
                              <FormatItem>{docking.input_dwt}</FormatItem>
                            </Item>
                            <Item label="Manobra acompanhada?">
                              {docking.docking_manoeuvre?.supervised}
                            </Item>
                            <Item label="Plantonista que acomp.">
                              <FormatItem>
                                {
                                  docking.docking_manoeuvre
                                    ?.on_duty_that_accompanied
                                }
                              </FormatItem>
                            </Item>
                            <Item label="Deslocamento na entrada">
                              <FormatItem>
                                {docking.entrance_displacement}
                              </FormatItem>
                            </Item>
                          </Descriptions>
                          <Descriptions column={3} title="Praticagem">
                            <Item label="Empresa" span={1}>
                              <FormatItem>
                                {docking.docking_manoeuvre?.pilotage_company}
                              </FormatItem>
                            </Item>
                            <Item label="Período a bordo" span={2}>
                              <div>
                                <FormatItem>
                                  {
                                    docking.docking_manoeuvre
                                      ?.start_period_on_board
                                  }
                                </FormatItem>
                                <span> até </span>
                                <FormatItem>
                                  {
                                    docking.docking_manoeuvre
                                      ?.end_period_on_board
                                  }
                                </FormatItem>
                              </div>
                            </Item>
                            <Item label="Práticos" span={2}>
                              <FormatItem>
                                {docking.docking_manoeuvre?.pilots}
                              </FormatItem>
                            </Item>
                            <Item label="Praticante" span={1}>
                              <FormatItem>
                                {docking.docking_manoeuvre?.practitioner}
                              </FormatItem>
                            </Item>
                            <Item label="Fez navegação interna?" span={1}>
                              {
                                docking.docking_manoeuvre
                                  ?.did_internal_navigation
                              }
                            </Item>
                            <Item label="Início da navegação interna" span={2}>
                              <FormatItem>
                                {docking.start_of_internal_navigation}
                              </FormatItem>
                            </Item>
                          </Descriptions>
                          <Descriptions column={3} title="Rebocagem">
                            <Item label="Empresa">
                              <FormatItem>
                                {docking.docking_manoeuvre?.towage_company}
                              </FormatItem>
                            </Item>
                            <Item label="Rebocadores" span={2}>
                              <div className="span-neutral-medium">
                                <FormatItem>
                                  {docking.docking_manoeuvre?.bow_tugboat}
                                </FormatItem>
                                <span>(proa), </span>
                                <FormatItem>
                                  {docking.docking_manoeuvre?.stern_tugboat}
                                </FormatItem>
                                <span>(popa), </span>
                                <FormatItem>
                                  {docking.docking_manoeuvre?.standby_tugboat}
                                </FormatItem>
                                <span>(stand by), </span>
                                <FormatItem>
                                  {docking.docking_manoeuvre?.amidship_tugboat}
                                </FormatItem>
                                <span>(meia-nau)</span>
                              </div>
                            </Item>
                          </Descriptions>
                          <Descriptions column={3} title="Amarração">
                            <Item label="Empresa">
                              <FormatItem>
                                {docking.docking_manoeuvre?.mooring_company}
                              </FormatItem>
                            </Item>
                            <Item label="Amarrado" span={2}>
                              <div>
                                <FormatItem>{docking.start_tied}</FormatItem>
                                <span> até </span>
                                <FormatItem>{docking.end_tied}</FormatItem>
                              </div>
                            </Item>
                          </Descriptions>
                          <Divider />
                          <Title>CABEÇOS E DEFENSAS</Title>
                          <Descriptions column={3} title="Proa">
                            <Item label="Cabeços" span={2}>
                              <div className="span-neutral-medium">
                                <FormatItem>
                                  {docking.bollard_bow_launcher}
                                </FormatItem>
                                <span>(lançante), </span>
                                <FormatItem>
                                  {docking.bollard_bow_spring}
                                </FormatItem>
                                <span>(spring), </span>
                                <FormatItem>
                                  {docking.bollard_bow_across}
                                </FormatItem>
                                <span>(través)</span>
                              </div>
                            </Item>
                            <Item label="Primeira defensa de apoio">
                              <FormatItem>
                                {docking.first_bow_support_fender}
                              </FormatItem>
                            </Item>
                          </Descriptions>
                          <Descriptions column={3} title="Popa">
                            <Item label="Cabeços" span={2}>
                              <div className="span-neutral-medium">
                                <FormatItem>
                                  {docking.bollard_stern_launcher}
                                </FormatItem>
                                <span>(lançante), </span>
                                <FormatItem>
                                  {docking.bollard_stern_spring}
                                </FormatItem>
                                <span>(spring), </span>
                                <FormatItem>
                                  {docking.bollard_stern_across}
                                </FormatItem>
                                <span>(través)</span>
                              </div>
                            </Item>
                            <Item label="Última defensa de apoio">
                              <FormatItem>
                                {docking.last_stern_support_fender}
                              </FormatItem>
                            </Item>
                          </Descriptions>

                          <Divider />
                          <Title>MANOBRA DE DESATRACAÇÃO</Title>
                          <Descriptions column={3}>
                            <Item label="Manobra confirmada?" span={3}>
                              {docking.undocking_manoeuvre?.confirmed}
                            </Item>
                            <Item label="Calado de proa">
                              <FormatItem>
                                {docking.undocking_manoeuvre?.bow_draught}
                              </FormatItem>
                            </Item>
                            <Item label="Calado de popa">
                              <FormatItem>
                                {docking.undocking_manoeuvre?.stern_draught}
                              </FormatItem>
                            </Item>
                            <Item label="Calado na saída">
                              <FormatItem>{docking.output_draught}</FormatItem>
                            </Item>
                          </Descriptions>
                          <Descriptions column={3}>
                            <Item label="DWT na saída">
                              <FormatItem>{docking.output_dwt}</FormatItem>
                            </Item>
                            <Item label="Manobra acompanhada?">
                              {docking.undocking_manoeuvre?.supervised}
                            </Item>
                            <Item label="Plantonista que acomp.">
                              <FormatItem>
                                {
                                  docking.undocking_manoeuvre
                                    ?.on_duty_that_accompanied
                                }
                              </FormatItem>
                            </Item>
                            <Item label="Deslocamento na saída">
                              <FormatItem>
                                {docking.exit_displacement}
                              </FormatItem>
                            </Item>
                          </Descriptions>
                          <Descriptions column={3} title="Praticagem">
                            <Item label="Empresa" span={1}>
                              <FormatItem>
                                {docking.undocking_manoeuvre?.pilotage_company}
                              </FormatItem>
                            </Item>
                            <Item label="Período a bordo" span={2}>
                              <div>
                                <FormatItem>
                                  {
                                    docking.undocking_manoeuvre
                                      ?.start_period_on_board
                                  }
                                </FormatItem>
                                <span> até </span>
                                <FormatItem>
                                  {
                                    docking.undocking_manoeuvre
                                      ?.end_period_on_board
                                  }
                                </FormatItem>
                              </div>
                            </Item>
                            <Item label="Práticos" span={2}>
                              <FormatItem>
                                {docking.undocking_manoeuvre?.pilots}
                              </FormatItem>
                            </Item>
                            <Item label="Praticante" span={1}>
                              <FormatItem>
                                {docking.undocking_manoeuvre?.practitioner}
                              </FormatItem>
                            </Item>
                            <Item label="Navio saiu na barra?" span={1}>
                              {
                                docking.undocking_manoeuvre
                                  ?.did_departure_at_port_entrance
                              }
                            </Item>
                            <Item label="Horário de saída na barra" span={2}>
                              <FormatItem>
                                {docking.departure_time_at_port_entrance}
                              </FormatItem>
                            </Item>
                          </Descriptions>
                          <Descriptions column={3} title="Rebocagem">
                            <Item label="Empresa" span={1}>
                              <FormatItem>
                                {docking.undocking_manoeuvre?.towage_company}
                              </FormatItem>
                            </Item>
                            <Item label="Rebocadores" span={2}>
                              <div className="span-neutral-medium">
                                <FormatItem>
                                  {docking.undocking_manoeuvre?.bow_tugboat}
                                </FormatItem>
                                <span>(proa), </span>
                                <FormatItem>
                                  {docking.undocking_manoeuvre?.stern_tugboat}
                                </FormatItem>
                                <span>(popa), </span>
                                <FormatItem>
                                  {docking.undocking_manoeuvre?.standby_tugboat}
                                </FormatItem>
                                <span>(stand by), </span>
                                <FormatItem>
                                  {
                                    docking.undocking_manoeuvre
                                      ?.amidship_tugboat
                                  }
                                </FormatItem>
                                <span>(meia-nau)</span>
                              </div>
                            </Item>
                          </Descriptions>
                          <Descriptions column={3} title="Desamarração">
                            <Item label="Empresa" span={1}>
                              <FormatItem>
                                {docking.undocking_manoeuvre?.mooring_company}
                              </FormatItem>
                            </Item>
                            <Item label="Desamarração" span={2}>
                              <div>
                                <FormatItem>{docking.start_untied}</FormatItem>
                                <span> até </span>
                                <FormatItem>{docking.end_untied}</FormatItem>
                              </div>
                            </Item>
                          </Descriptions>
                          <Divider />
                        </>
                      )}
                      <TitleEdit>
                        <Title>RESPONSÁVEIS FINANCEIROS</Title>
                        {screen === 'AGENT_AREA' && (
                          <Button
                            type="primary"
                            icon={<EditFilled />}
                            onClick={(e) => {
                              setOpenCollapsePanels('financialOfficers');
                              onClickEdit(e, index);
                            }}
                          >
                            Editar
                          </Button>
                        )}
                      </TitleEdit>
                      <FinancialOfficersTableDescription
                        financialTables={docking.financial_tables}
                      />
                    </>
                  )}
                </div>
              </Panel>
            );
          })}
        </DockingItemsCollapse>
        {blockPagination()}
      </>
    );
  }

  return (
    <DockingEventBlockCollapse
      expandIconPosition="end"
      defaultActiveKey={block.defaultOpen ? [block.key] : []}
    >
      <Panel forceRender header={blockHeader()} key={block.key}>
        {blockContent()}
      </Panel>
    </DockingEventBlockCollapse>
  );
}
